import { useState } from 'react';
import { useScrollReveal } from '../../hooks/useScrollReveal';

export interface FAQItem {
  question: string;
  answer: string;
}

interface FAQProps {
  items: FAQItem[];
  title?: string;
}

export default function FAQ({ items, title = 'Frequently Asked Questions' }: FAQProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const headerRef = useScrollReveal();
  const listRef = useScrollReveal();

  return (
    <section className="py-36 bg-soft-white">
      <div className="max-w-7xl mx-auto px-8 lg:px-16">
        {/* Header */}
        <div ref={headerRef} className="reveal-on-scroll grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20">
          <div className="lg:col-span-5">
            <p className="font-sans text-[10px] tracking-[0.22em] uppercase text-bronze mb-4">Voice Search Ready</p>
            <h2 className="font-serif text-5xl lg:text-6xl font-light text-dark-ink leading-tight">{title}</h2>
          </div>
          <div className="lg:col-span-7 flex items-end">
            <p className="font-sans text-sm text-warm-gray leading-relaxed max-w-md">
              Answers to the most common questions about residential architecture — optimized for both voice search and direct clarity.
            </p>
          </div>
        </div>

        {/* Accordion */}
        <div ref={listRef} className="reveal-on-scroll reveal-delay-2 max-w-4xl">
          {items.map((faq, i) => (
            <div key={i} className="border-t border-stone last:border-b">
              <button
                className="w-full flex items-start justify-between py-7 text-left cursor-pointer group"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
              >
                <span className="font-serif text-xl font-light text-dark-ink group-hover:text-wood transition-colors duration-300 pr-8 leading-snug">
                  {faq.question}
                </span>
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5 border border-stone group-hover:border-wood transition-colors duration-300">
                  <i
                    className={`ri-add-line text-warm-gray group-hover:text-dark-ink transition-all duration-300 text-base ${
                      openIndex === i ? 'rotate-45' : ''
                    }`}
                  />
                </div>
              </button>
              <div
                className={`overflow-hidden transition-all duration-500 ${
                  openIndex === i ? 'max-h-96 pb-8' : 'max-h-0'
                }`}
              >
                <p className="font-sans text-sm text-warm-gray leading-[1.8] max-w-2xl">{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
